/**
 * GenerationFormImageUpload
 *
 * Image upload component with drag & drop, preview, and product info.
 */
import { useRef } from 'react';
import { Upload, X, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

interface GenerationFormImageUploadProps {
  selectedFile: File | null;
  preview: string | null;
  isDragOver: boolean;
  isLoading: boolean;
  productInfo: { id: string; name: string } | null;
  onFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onDrop: (event: React.DragEvent<HTMLDivElement>) => void;
  onDragOver: (event: React.DragEvent<HTMLDivElement>) => void;
  onDragLeave: () => void;
  onClear: () => void;
}

export function GenerationFormImageUpload({
  selectedFile: _selectedFile,
  preview,
  isDragOver,
  isLoading,
  productInfo,
  onFileSelect,
  onDrop,
  onDragOver,
  onDragLeave,
  onClear,
}: GenerationFormImageUploadProps) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (isLoading) {
    return (
      <div className="space-y-2">
        <label className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
          Produktbild
        </label>
        <div className="flex flex-col items-center justify-center p-8 rounded-lg border-2 border-dashed border-neutral-300 dark:border-neutral-700">
          <Loader2 className="h-8 w-8 animate-spin text-primary-500 mb-2" />
          <p className="text-sm text-neutral-500">Lade Produktbild...</p>
        </div>
      </div>
    );
  }

  if (preview) {
    return (
      <div className="space-y-2">
        <label className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
          Produktbild
        </label>
        <div className="relative rounded-lg overflow-hidden bg-neutral-100 dark:bg-neutral-800">
          <img src={preview} alt="Preview" className="w-full h-48 object-contain" />
          <button
            type="button"
            onClick={onClear}
            className="absolute top-2 right-2 p-1.5 rounded-full bg-red-500 text-white hover:bg-red-600 transition-colors"
            aria-label={t('common.remove')}
          >
            <X className="h-4 w-4" />
          </button>
          {productInfo && (
            <div className="absolute bottom-0 left-0 right-0 p-2 bg-black/70 text-white text-xs">
              {productInfo.name || `Produkt ${productInfo.id.slice(0, 8)}`}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
        Produktbild
      </label>
      <div
        onClick={() => fileInputRef.current?.click()}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        className={cn(
          'flex flex-col items-center justify-center p-8 rounded-lg border-2 border-dashed cursor-pointer transition-colors',
          isDragOver
            ? 'border-primary-500 bg-primary-50 dark:bg-primary-950/20'
            : 'border-neutral-300 dark:border-neutral-700 hover:border-primary-400'
        )}
      >
        <Upload className="h-8 w-8 text-neutral-400 mb-2" />
        <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-1">
          {t('common.dragDropOrClick')}
        </p>
        <p className="text-xs text-neutral-400">{t('common.maxFileSize16MB')}</p>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/webp"
        onChange={onFileSelect}
        className="hidden"
      />
    </div>
  );
}
